import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Timer, Play } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { GenreRow } from "@/components/GenreRow";
import { VideoCard } from "@/components/VideoCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchShorts, fetchShortsFacets, shuffleSeed } from "@/lib/api";

// Fresh order on every visit to the Shorts hub.
const SEED = { value: shuffleSeed() };

const GENRE_LABEL = { action: "Action", comedy: "Comedy", horror: "Horror", "sci-fi": "Sci-Fi", drama: "Drama", thriller: "Thriller",
  animation: "Animation", romance: "Romance", documentary: "Documentary" };
const AUDIENCE_LABEL = { family: "Family", teen: "Teen", mature: "Mature" };
const ALL = "all";

const FilterSelect = ({ id, value, onChange, options, labels, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={`shorts-${id}-select`} className="w-[190px] rounded-none border-white/15 bg-[#0B0B0B] font-mono text-[11px] uppercase tracking-[0.15em] text-white/80">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="border-white/10 bg-[#0B0B0B] text-white">
      <SelectItem value={ALL} data-testid={`shorts-${id}-all`}>{placeholder}</SelectItem>
      {Object.entries(options).map(([k, n]) => (
        <SelectItem key={k} value={k} data-testid={`shorts-${id}-${k}`}>{labels[k] || k} · {n}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const ShortsGrid = ({ genre, audience, onClear }) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["shorts-grid", genre, audience, SEED.value],
    queryFn: ({ pageParam = 1 }) => fetchShorts({ page: pageParam, genre, audience, seed: SEED.value }),
    getNextPageParam: (last) => (last.page < last.total_pages ? last.page + 1 : undefined),
    initialPageParam: 1,
  });
  const items = data?.pages.flatMap((p) => p.items) || [];
  const total = data?.pages?.[0]?.total ?? 0;
  if (!isLoading && !items.length) {
    return (
      <div data-testid="shorts-empty" className="py-16">
        <p className="font-serif text-2xl text-white/70">No shorts match both filters yet.</p>
        <button data-testid="shorts-clear-filters" onClick={onClear} className="mt-5 border border-amber/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-amber transition-colors duration-300 hover:bg-amber hover:text-black">
          Show all shorts
        </button>
      </div>
    );
  }
  return (
    <>
      {!isLoading && (
        <p data-testid="shorts-match-count" className="mb-6 font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          {total} short{total === 1 ? "" : "s"} match
        </p>
      )}
      <div data-testid="shorts-grid" className="grid grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-3 lg:grid-cols-4 min-[1600px]:grid-cols-5 min-[2200px]:grid-cols-6">
        {items.map((v) => <VideoCard key={v.video_id} video={v} />)}
      </div>
      {hasNextPage && (
        <button data-testid="shorts-load-more" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
          className="mt-10 border border-amber/50 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:bg-amber hover:text-black transition-colors duration-300">
          {isFetchingNextPage ? "Loading…" : "Load more shorts"}
        </button>
      )}
    </>
  );
};

const ShortsRows = ({ facets }) => (
  <>
    {Object.keys(facets.genres).map((g) => (
      <GenreRow key={g} title={`${GENRE_LABEL[g] || g} Shorts`} tag={`shorts-${g}`} queryFn={() => fetchShorts({ genre: g, limit: 15, seed: SEED.value })} />
    ))}
    {Object.keys(facets.audiences).map((a) => (
      <GenreRow key={a} title={`${AUDIENCE_LABEL[a]} Shorts`} tag={`shorts-aud-${a}`} queryFn={() => fetchShorts({ audience: a, limit: 15, seed: SEED.value })} />
    ))}
  </>
);

export default function Shorts() {
  useState(() => {
    SEED.value = shuffleSeed();
  });
  const [genre, setGenre] = useState(ALL);
  const [audience, setAudience] = useState(ALL);
  const { data: facets } = useQuery({ queryKey: ["shorts-facets"], queryFn: fetchShortsFacets });
  const g = genre === ALL ? undefined : genre;
  const a = audience === ALL ? undefined : audience;
  // Dropdown counts depend on the other filter, so they always match the combined result.
  const { data: ctx } = useQuery({
    queryKey: ["shorts-facets", g, a],
    queryFn: () => fetchShortsFacets({ genre: g, audience: a }),
    placeholderData: (prev) => prev,
  });
  const keep = (opts, v) => (v === ALL || !opts ? opts || {} : { ...opts, [v]: opts[v] ?? 0 });
  const filtering = genre !== ALL || audience !== ALL;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="shorts-page">
      <Helmet>
        <title>Shorts · ReelVault</title>
        <meta name="description" content="Free short films under 20 minutes — action, comedy, horror and more." />
      </Helmet>
      <Header />
      <main className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-10">
        <div className="mb-10 flex flex-wrap items-end gap-6">
          <div className="mr-auto">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber"><Timer size={16} weight="duotone" /> Under 20 minutes</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">Shorts</h1>
            {facets && <p data-testid="shorts-total" className="mt-2 font-mono text-xs text-white/40">{facets.total} short films</p>}
          </div>
          {facets && (
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/shorts/feed?${new URLSearchParams({ ...(genre !== ALL && { genre }), ...(audience !== ALL && { audience }) })}`}
                data-testid="shorts-play-feed"
                className="flex items-center gap-2 rounded-full bg-amber px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
              >
                <Play size={14} weight="fill" /> Play Shorts Feed
              </Link>
              <FilterSelect id="genre" value={genre} onChange={setGenre} options={keep(ctx?.genres, genre)} labels={GENRE_LABEL} placeholder="All genres" />
              <FilterSelect id="audience" value={audience} onChange={setAudience} options={keep(ctx?.audiences, audience)} labels={AUDIENCE_LABEL} placeholder="All ratings" />
            </div>
          )}
        </div>
        {filtering ? (
          <ShortsGrid genre={g} audience={a} onClear={() => { setGenre(ALL); setAudience(ALL); }} />
        ) : (
          facets && <ShortsRows facets={facets} />
        )}
      </main>
    </div>
  );
}
